import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { collection, getDocs } from 'firebase/firestore'
import {
  Box,
  Flex,
  Heading,
  Text,
  Image,
  Link as ChakraLink,
} from '@chakra-ui/react'
import { db } from '../config/firebase'
import logoImg from '../assets/images/logo_flag.png'
import { Footer } from '../components/Footer'

const SITES_COLLECTION = 'sites'

const PromoPage = () => {
  const [sites, setSites] = useState<
    Array<{ id: string; name?: string; url: string; order?: number }>
  >([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchSites = async () => {
      if (!db) {
        setLoading(false)
        return
      }
      try {
        const snapshot = await getDocs(collection(db, SITES_COLLECTION))
        const list = snapshot.docs
          .map((d) => ({
            id: d.id,
            name: d.data().name,
            url: d.data().url,
            order: d.data().order ?? 999999,
          }))
          .sort((a, b) => (a.order ?? 999999) - (b.order ?? 999999))
        setSites(list)
      } catch (err) {
        console.error('홍보페이지 로드 실패:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchSites()
  }, [])

  const HeaderContent = () => (
    <Flex alignItems="center" justifyContent="center" gap={3}>
      <Box
        w={8}
        h={8}
        borderRadius="lg"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="white"
        boxShadow="sm"
        border="1px solid"
        borderColor="gray.200"
      >
        <Image src={logoImg} alt="EXCELLO" w={5} h={5} objectFit="contain" />
      </Box>
      <Heading as="h2" size="lg" letterSpacing="-0.015em" color="gray.900">
        EXCELLO
      </Heading>
    </Flex>
  )

  const BackLink = () => (
    <Flex mt={12} justifyContent="center">
      <Link to="/">
        <Flex
          as="span"
          alignItems="center"
          gap={2}
          fontSize="sm"
          fontWeight="medium"
          color="gray.500"
          _hover={{ color: 'blue.500' }}
        >
          <Box as="span" className="material-symbols-outlined" fontSize="xl">
            arrow_back
          </Box>
          Back to main portal
        </Flex>
      </Link>
    </Flex>
  )

  if (loading) {
    return (
      <Box minH="100vh" display="flex" flexDirection="column" bg="gray.50">
        <Box
          as="header"
          position="sticky"
          top={0}
          zIndex={50}
          w="100%"
          bg="white"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Flex maxW="1280px" mx="auto" py={4} px={6} justifyContent="center">
            <HeaderContent />
          </Flex>
        </Box>
        <Box as="main" flex={1} maxW="800px" mx="auto" px={6} py={12} pb={24}>
          <Text textAlign="center" color="gray.500" fontSize="lg" py={12}>
            불러오는 중...
          </Text>
          <BackLink />
        </Box>
        <Footer />
      </Box>
    )
  }

  if (sites.length === 0) {
    return (
      <Box minH="100vh" display="flex" flexDirection="column" bg="gray.50">
        <Box
          as="header"
          position="sticky"
          top={0}
          zIndex={50}
          w="100%"
          bg="white"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Flex maxW="1280px" mx="auto" py={4} px={6} justifyContent="center">
            <HeaderContent />
          </Flex>
        </Box>
        <Box as="main" flex={1} maxW="800px" mx="auto" px={6} py={12} pb={24}>
          <Text textAlign="center" color="gray.500" fontSize="lg" py={12}>
            등록된 홍보페이지가 없습니다.
          </Text>
          <BackLink />
        </Box>
        <Footer />
      </Box>
    )
  }

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg="gray.50">
      <Box
        as="header"
        position="sticky"
        top={0}
        zIndex={50}
        w="100%"
        bg="white"
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <Flex maxW="1280px" mx="auto" py={4} px={6} justifyContent="center">
          <HeaderContent />
        </Flex>
      </Box>

      <Box as="main" flex={1} maxW="800px" mx="auto" px={6} py={12} pb={24}>
        <Box mb={12} textAlign="center">
          <Heading
            as="h1"
            size={{ base: 'lg', md: 'xl' }}
            color="gray.900"
            mb={4}
          >
            Our Digital Platforms
          </Heading>
          <Text fontSize="lg" color="gray.500">
            Explore all our websites and digital platforms.
          </Text>
        </Box>

        <Box
          bg="white"
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.200"
          boxShadow="sm"
          overflow="hidden"
        >
          <Flex
            display={{ base: 'none', md: 'flex' }}
            alignItems="center"
            justifyContent="space-between"
            px={8}
            py={4}
            bg="gray.50"
            borderBottom="1px solid"
            borderColor="gray.200"
          >
            <Text
              fontSize="xs"
              fontWeight="bold"
              textTransform="uppercase"
              letterSpacing="wider"
              color="gray.500"
            >
              Platform Name
            </Text>
            <Text
              fontSize="xs"
              fontWeight="bold"
              textTransform="uppercase"
              letterSpacing="wider"
              color="gray.500"
            >
              Access Link
            </Text>
          </Flex>

          {sites.map((site, index) => (
            <Flex
              key={site.id}
              direction={{ base: 'column', md: 'row' }}
              align={{ base: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              gap={3}
              px={{ base: 6, md: 8 }}
              py={6}
              borderBottom={index < sites.length - 1 ? '1px solid' : 'none'}
              borderColor="gray.200"
              _hover={{ bg: 'gray.50' }}
              transition="background 0.2s"
            >
              <Flex alignItems="flex-start" gap={4} flex={1} minW={0}>
                <Flex
                  w={10}
                  h={10}
                  minW={10}
                  borderRadius="lg"
                  alignItems="center"
                  justifyContent="center"
                  bg="blue.50"
                  color="blue.500"
                >
                  <Box
                    as="span"
                    className="material-symbols-outlined"
                    fontSize="xl"
                  >
                    language
                  </Box>
                </Flex>
                <Box flex={1} minW={0}>
                  <Heading as="h3" size="sm" color="gray.900" mb={1}>
                    {site.name ?? '이름 없음'}
                  </Heading>
                  <Text fontSize="xs" color="gray.500">
                    External site
                  </Text>
                </Box>
              </Flex>
              <ChakraLink
                href={site.url}
                isExternal
                fontSize="sm"
                fontWeight="bold"
                color="blue.500"
                display="flex"
                alignItems="center"
                gap={2}
                wordBreak="break-all"
                _hover={{ textDecoration: 'underline' }}
              >
                {site.url}
                <Box
                  as="span"
                  className="material-symbols-outlined"
                  fontSize="sm"
                >
                  open_in_new
                </Box>
              </ChakraLink>
            </Flex>
          ))}
        </Box>

        <BackLink />
      </Box>

      <Footer />
    </Box>
  )
}

export default PromoPage
